<template>
  <div class="legal-doc min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <div class="text-center mb-12">
        <h1 class="legal-doc-title font-heading text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-[0_0_15px_rgba(0,242,255,0.3)]">
          {{ title }}
        </h1>
      </div>

      <div class="legal-doc-card bg-[#15161a] border border-white/5 rounded-lg p-8 md:p-12 shadow-2xl backdrop-blur-sm relative overflow-hidden">
        <div class="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div class="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] pointer-events-none"></div>

        <!-- Loading -->
        <div v-if="pending" class="flex justify-center py-20">
          <div class="w-10 h-10 border-3 border-white/10 border-t-primary rounded-full animate-spin"></div>
        </div>

        <!-- Configured content (admin-authored, sanitized server-side) -->
        <div v-else-if="content" class="relative">
          <div
            class="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-white prose-p:text-text-dim prose-strong:text-white prose-li:text-text-dim prose-a:text-primary hover:prose-a:text-primary/80"
            v-html="content"
          ></div>
          <div v-if="updatedAt" class="mt-12 pt-8 border-t border-white/5 text-center">
            <ClientOnly>
              <p class="text-xs text-text-muted">{{ $t('legal.lastUpdated', 'As of') }}: {{ fmt(updatedAt) }}</p>
              <template #fallback><p class="text-xs text-text-muted">—</p></template>
            </ClientOnly>
          </div>
        </div>

        <!-- Built-in fallback (e.g. the static Widerruf/Versand/Rückgabe text)
             rendered when no content is configured in the admin editor. -->
        <div v-else-if="hasFallback" class="relative space-y-6 text-text-dim leading-relaxed">
          <slot />
        </div>

        <!-- Nothing configured + no fallback -->
        <div v-else class="text-center py-20 text-text-muted">
          <p class="text-lg">{{ emptyText }}</p>
          <p class="text-sm mt-2">{{ $t('legal.checkBackLater', 'Please check back later or contact the operator.') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, useSlots } from 'vue'

const props = defineProps({
  type: { type: String, required: true },
  title: { type: String, default: '' },
  emptyText: { type: String, default: 'This text has not been published yet.' }
})

const slots = useSlots()
const hasFallback = computed(() => !!slots.default)

const { locale } = useI18n()
const route = useRoute()
// The merchant variant is a DIFFERENT document, so it needs its own cache key —
// sharing one would serve whichever of the two was fetched first.
const merchantScoped = computed(() => route.query.merchant === '1')

const { data, pending } = await useFetch('/api/public/legal', {
  key: computed(() => `legal-${props.type}-${merchantScoped.value ? 'merchant' : 'site'}`),
  // `?merchant=1` when the page is reached from a buy surface: those sales are
  // Diztree's, so the terms and the withdrawal notice have to be Diztree's too.
  query: computed(() => ({
    type: props.type,
    locale: locale.value,
    ...(merchantScoped.value ? { merchant: '1' } : {})
  })),
  default: () => ({ content: '', updatedAt: null })
})

const content = computed(() => data.value?.content || '')
const updatedAt = computed(() => data.value?.updatedAt || null)

function fmt(d) {
  try { return new Date(d).toLocaleDateString({ de: 'de-DE', pt: 'pt-BR', es: 'es-ES' }[locale.value] || 'en-GB') } catch { return '—' }
}
</script>
